import React, { useState, useEffect } from 'react';
import { Languages, Send, Loader, AlertCircle, Copy, Check } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { getProjectData, saveProjectData, getGlobalData } from '../utils/projectStorage';
import './McodeTranslation.css';

interface McodeTranslationData {
  settings?: {
    generalInstructionsPrompt: string;
    mcodeDimensionsFile?: {
      name: string;
      content: string;
      lastModified: number;
      size: number;
    };
  };
  ingestedContent?: string;
  messageHistory?: Array<{
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
  }>;
  responses?: Array<{
    id: string;
    question: string;
    dimension: string;
    response: string;
    timestamp: Date;
  }>;
}

const motivationalDimensions = [
  'Achiever', 'Driver', 'Influencer', 'Learner',
  'Optimizer', 'Orchestrator', 'Relator', 'Visionary'
];

const McodeTranslation: React.FC = () => {
  const [question, setQuestion] = useState('');
  const [selectedDimension, setSelectedDimension] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [response, setResponse] = useState('');
  const [projectData, setProjectData] = useState<McodeTranslationData>({});
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    // Load project data
    const data = getProjectData('mcode-translation');
    setProjectData(data);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!question.trim() || !selectedDimension) return;

    const globalData = getGlobalData();
    if (!globalData.apiKey) {
      setError('Please configure your Anthropic API key in settings.');
      return;
    }

    // Check if settings have been processed
    const ingestedContent = projectData.ingestedContent;
    if (!ingestedContent) {
      setError('Please configure and process settings first in the Settings tab.');
      return;
    }

    setIsLoading(true);
    setError('');
    setResponse('');

    try {
      const Anthropic = (await import('@anthropic-ai/sdk')).default;
      const anthropic = new Anthropic({
        apiKey: globalData.apiKey,
        dangerouslyAllowBrowser: true
      });

      const prompt = `Customer Motivational Dimension: ${selectedDimension}

Translation Question/Challenge:
${question}`;

      // Get project-specific message history for context
      let previousMessages: any[] = [];
      
      if (projectData.messageHistory) {
        try {
          // Include relevant previous messages for context
          previousMessages = projectData.messageHistory.slice(-10).map((msg: any) => ({
            role: msg.role,
            content: msg.content
          }));
        } catch (error) {
          console.error('Error parsing message history:', error);
        }
      }

      const response = await anthropic.messages.create({
        model: 'claude-sonnet-4-20250514',
        max_tokens: 4096,
        system: [
          {
            type: 'text',
            text: ingestedContent,
            cache_control: { type: 'ephemeral' }
          }
        ],
        messages: [
          ...previousMessages,
          {
            role: 'user',
            content: prompt
          }
        ]
      });

      const responseText = response.content[0].type === 'text' ? response.content[0].text : '';
      setResponse(responseText);

      // Save response to project data
      const newResponse = {
        id: Date.now().toString(),
        question,
        dimension: selectedDimension,
        response: responseText,
        timestamp: new Date()
      };
      
      const updatedData = {
        ...projectData,
        responses: [...(projectData.responses || []), newResponse]
      };
      
      setProjectData(updatedData);
      saveProjectData('mcode-translation', updatedData);

      // Update project-specific message history
      const updatedHistory = [
        ...(projectData.messageHistory || []),
        {
          id: Date.now().toString(),
          role: 'user' as const,
          content: `[MCODE Translation - ${selectedDimension}] ${question}`,
          timestamp: new Date()
        },
        {
          id: (Date.now() + 1).toString(),
          role: 'assistant' as const,
          content: responseText,
          timestamp: new Date()
        }
      ];
      
      updatedData.messageHistory = updatedHistory;
      
    } catch (err: any) {
      console.error('Error calling Claude API:', err);
      setError(err.message || 'An error occurred while processing your request');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  return (
    <div className="mcode-translation">
      <div className="mcode-translation-content">
        <form onSubmit={handleSubmit} className="question-form">
          <div className="form-group">
            <label htmlFor="question">Translation Question</label>
            <textarea
              id="question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              rows={4}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="dimension">Motivational Dimension</label>
            <select
              id="dimension"
              value={selectedDimension}
              onChange={(e) => setSelectedDimension(e.target.value)}
              required
            >
              <option value="">Select a motivational dimension...</option>
              {motivationalDimensions.map(dim => (
                <option key={dim} value={dim}>{dim}</option>
              ))}
            </select>
          </div>

          <button type="submit" disabled={isLoading || !question.trim() || !selectedDimension}>
            {isLoading ? (
              <>
                <Loader className="spinner" size={16} />
                Translating...
              </>
            ) : (
              <>
                <Send size={16} />
                Generate Translation
              </>
            )}
          </button>
        </form>

        {error && (
          <div className="error-message">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {response && (
          <div className="response-section">
            <div className="response-header">
              <h3>Translation Analysis</h3>
              <button
                onClick={() => copyToClipboard(response, 'current')}
                className="copy-button"
                title="Copy response"
              >
                {copiedId === 'current' ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
            <div className="response-content">
              <ReactMarkdown>{response}</ReactMarkdown>
            </div>
          </div>
        )}

        {projectData.responses && projectData.responses.length > 0 && (
          <div className="previous-responses">
            <h3>Previous Translation Analyses</h3>
            {projectData.responses.slice().reverse().map((resp) => (
              <div key={resp.id} className="response-item">
                <div className="response-meta">
                  <span className="dimension-tag">{resp.dimension}</span>
                  <span className="timestamp">{new Date(resp.timestamp).toLocaleString()}</span>
                  <button
                    onClick={() => copyToClipboard(resp.response, resp.id)}
                    className="copy-button"
                    title="Copy response"
                  >
                    {copiedId === resp.id ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                </div>
                <div className="question-text">
                  <strong>Q:</strong> {resp.question}
                </div>
                <div className="response-content">
                  <ReactMarkdown>{resp.response}</ReactMarkdown>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default McodeTranslation;